/**
 * ActionsMenu Component
 *
 * Dropdown menu for node actions. Displays customizable list of actions
 * with support for icons, disabled states, and dividers.
 *
 * All styling via MUI theme - NO hardcoded colors.
 */

import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';

import { NodeAction } from '../../types/node-actions';

export interface ActionsMenuProps {
  /** Anchor element for menu positioning */
  anchorEl: HTMLElement | null;

  /** Whether menu is open */
  open: boolean;

  /** Close menu callback */
  onClose: () => void;

  /** List of actions to display */
  actions: NodeAction[];

  /** Callback when action is clicked */
  onActionClick: (actionKey: string) => void;
}

/**
 * ActionsMenu - Dropdown menu for node toolbar actions
 *
 * Features:
 * - Customizable actions via props
 * - Icon support
 * - Disabled states
 * - Dividers between sections
 * - Theme-only colors (no hardcoded values)
 */
export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  anchorEl,
  open,
  onClose,
  actions,
  onActionClick,
}) => {
  const handleActionClick = (actionKey: string, disabled?: boolean) => {
    if (disabled) return;

    onActionClick(actionKey);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'background.paper',
            color: 'text.primary',
            minWidth: 180,
            boxShadow: 3,
          },
        },
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      {actions.map((action, index) => (
        <React.Fragment key={action.key}>
          <MenuItem
            onClick={() => handleActionClick(action.key, action.disabled)}
            disabled={action.disabled}
            data-testid={`menu-action-${action.key}`}
            sx={{
              color: action.color === 'error' ? 'error.main' : 'text.primary',
              '&:hover': {
                backgroundColor:
                  action.color === 'error'
                    ? 'error.light'
                    : 'action.hover',
              },
            }}
          >
            {action.icon && (
              <ListItemIcon
                sx={{
                  color:
                    action.color === 'error'
                      ? 'error.main'
                      : action.color === 'primary'
                        ? 'primary.main'
                        : 'text.secondary',
                }}
              >
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText>{action.title}</ListItemText>
          </MenuItem>

          {action.divider && index < actions.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Menu>
  );
};
